import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Play, BookOpen, ExternalLink, Bell, CheckCircle2 } from "lucide-react";
import { TRAILERS, EMAGAZINE, FACEBOOK_FEATURE } from "../lib/constants";
import { notifyMe } from "../lib/api";
import { toast } from "sonner";

export default function YouTubePage() {
  const [activeVideo, setActiveVideo] = useState(TRAILERS[0]);
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [signed, setSigned] = useState(false);

  const handleNotify = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Drop a valid email so we can ping you at launch.");
      return;
    }
    setSubmitting(true);
    try {
      await notifyMe(email);
      setSigned(true);
      toast.success("You're on the launch list. We'll ping you.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Try again.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="youtube-page" className="min-h-screen bg-[#1A1414] text-white pt-20 sm:pt-24 pb-20 relative overflow-hidden">
      {/* Cinematic backdrop */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(circle at 20% 0%, rgba(230,0,0,0.35), transparent 50%), radial-gradient(circle at 80% 100%, rgba(255,128,128,0.18), transparent 55%)",
        }}
      />
      <div className="absolute inset-0 bg-grain opacity-10 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          data-testid="back-to-landing"
          className="inline-flex items-center gap-2 text-sm text-white/60 hover:text-[#FF8080] transition-colors mb-4"
        >
          <ArrowLeft size={16} /> Back to the worlds
        </Link>

        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FF8080] mb-2">
            Pillar 02 / Cinematic Universe
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-balance">
            YouTube <span className="text-[#FF8080]">Series.</span>
          </h1>
          <p className="mt-3 text-base text-white/70 max-w-2xl">
            The cinematic arm of Arkweaver — culturally relevant heroes facing the
            exact digital and social pressures grade 6–12 students live every day.
          </p>
        </div>

        {/* Main player */}
        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <div>
            <div className="relative aspect-video rounded-3xl overflow-hidden border-2 border-white/10 shadow-arc-lg bg-black">
              <iframe
                key={activeVideo.id}
                data-testid="youtube-player"
                src={`https://www.youtube-nocookie.com/embed/${activeVideo.id}?rel=0&modestbranding=1`}
                title={activeVideo.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
              />
            </div>
            <div className="mt-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF8080]">
                  Now playing — {activeVideo.kind}
                </p>
                <h2 className="font-display font-bold text-xl mt-1">{activeVideo.title}</h2>
              </div>
            </div>
          </div>

          {/* Playlist */}
          <aside className="space-y-3" data-testid="trailer-playlist">
            <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-[#FF8080]">
              Playlist
            </p>
            {TRAILERS.map((t) => {
              const isActive = t.id === activeVideo.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveVideo(t)}
                  data-testid={`trailer-${t.id}`}
                  className={`w-full flex gap-3 items-center p-3 rounded-2xl border transition-all text-left ${
                    isActive
                      ? "bg-[#E60000] border-[#E60000] text-white shadow-arc"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                  }`}
                >
                  <div className="relative w-24 aspect-video rounded-lg overflow-hidden shrink-0 bg-black">
                    <img
                      src={`https://i.ytimg.com/vi/${t.id}/mqdefault.jpg`}
                      alt={t.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                      <Play size={18} className="text-white fill-white" />
                    </div>
                  </div>
                  <div className="min-w-0">
                    <p className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
                      isActive ? "text-white/80" : "text-[#FF8080]"
                    }`}>
                      {t.kind}
                    </p>
                    <p className="text-sm font-display font-bold leading-tight truncate">
                      {t.title}
                    </p>
                  </div>
                </button>
              );
            })}
          </aside>
        </div>

        {/* Below the fold */}
        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {/* Notify me */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 sm:p-8 rounded-3xl bg-gradient-to-br from-[#8B0000] to-[#E60000] relative overflow-hidden"
            data-testid="notify-card"
          >
            <div className="absolute inset-0 bg-grain opacity-20 pointer-events-none" />
            <div className="relative">
              <Bell size={28} className="text-white mb-3" />
              <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
                Get pinged when we launch.
              </h3>
              <p className="text-sm text-white/85 mt-2 max-w-md">
                Drop your email. We&apos;ll send you the premiere date, exclusive
                episodes, and Bite Force drops — no spam, ever.
              </p>

              {signed ? (
                <div className="mt-5 inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#8B0000] font-display font-bold">
                  <CheckCircle2 size={18} /> You&apos;re on the launch list.
                </div>
              ) : (
                <form
                  onSubmit={handleNotify}
                  className="mt-5 flex flex-col sm:flex-row gap-3 max-w-md"
                  data-testid="notify-form"
                >
                  <input
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="notify-email-input"
                    className="flex-1 px-4 py-3 rounded-full bg-white/95 text-[#1A1414] placeholder:text-[#1A1414]/40 border-2 border-transparent focus:border-white focus:outline-none transition-colors"
                  />
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="notify-submit-btn"
                    className="px-6 py-3 rounded-full bg-[#1A1414] text-white font-display font-bold shadow-arc hover:bg-black hover:-translate-y-0.5 transition-all disabled:opacity-60"
                  >
                    {submitting ? "Adding…" : "Notify me"}
                  </button>
                </form>
              )}
            </div>
          </motion.div>

          {/* e-Magazine */}
          <motion.a
            href={EMAGAZINE.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="emagazine-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 sm:p-8 rounded-3xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all group block"
          >
            <BookOpen size={28} className="text-[#FF8080] mb-3" />
            <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
              Bite Force <span className="text-[#FF8080]">e‑Magazine.</span>
            </h3>
            <p className="text-sm text-white/70 mt-2 max-w-md">
              The space where youth express concerns, publish their voices, and
              engage in real time — extending the YouTube Series universe.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 font-display font-bold text-[#FF8080] group-hover:gap-3 transition-all">
              Open the magazine <ExternalLink size={16} />
            </span>
          </motion.a>

          {/* Facebook feature */}
          <motion.a
            href={FACEBOOK_FEATURE.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="facebook-feature-card"
            className="md:col-span-2 p-6 sm:p-8 rounded-3xl bg-white text-[#1A1414] hover:shadow-arc-lg transition-all group block"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#8B0000] mb-2">
              Featured on Facebook
            </p>
            <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tight">
              &ldquo;How to Deal with Bullies&rdquo; — watch the feature.
            </h3>
            <span className="mt-3 inline-flex items-center gap-2 font-display font-bold text-[#E60000] group-hover:gap-3 transition-all">
              Watch on Facebook <ExternalLink size={16} />
            </span>
          </motion.a>
        </div>
      </div>
    </div>
  );
}
